import React, { useCallback, useState, useEffect, useRef } from "react";
import { Typography, Box, IconButton, useTheme, Chip, Tooltip } from "@material-ui/core";
import { Annotation } from "store/features/annotations/interfaces";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { CommentContainer } from "pages/MaterialSpecs/components/InfoPanel/Annotations/Comment/CommentContainer.styled";
import ColorLensIcon from "@material-ui/icons/ColorLens";
import DeleteIcon from "@material-ui/icons/Delete";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { ConfirmationDialog, DropdownMenuItem, TextField } from "components";
import { ANNOTATIONS_FRAME_COLORS } from "pages/MaterialSpecs/components/PreviewSelector";
import { useDebounce, usePermissions } from "hooks";
import { useTranslation } from "react-i18next";
import Checkbox from "@material-ui/core/Checkbox";
import { isElementInViewport, toHHMMSS } from "utils";
import { ButtonDropdownColors } from "components/ButtonDropdown/ButtonDropdownColors";
import { useSelector } from "react-redux";
import { getAnnotationsStatus } from "store/features/annotations/annotations.selectors";
import { LoadingOverlay } from "components/LoadingOverlay/LoadingOverlay";
import { AssetType } from "api/assets";
import { useAnnotationsHandler, useMaterialSpecs } from "pages/MaterialSpecs/hooks";
import { useDownloadAsset } from "hooks/api";

interface CommentProps {
    annotation: Annotation;
    canEdit: boolean;
    selected?: boolean;
    onContainerSelect: (annotation: Annotation) => void;
    onRemoveAnnotation: (annotation: Annotation) => void;
    onRemoveAsset: (assetId: number, selectionId: number) => void;
    onUpdate: (annotation: Annotation) => void;
    onIsReviewedUpdate: (annotation: Annotation) => void;
    onAttachAsset(selectionId: number, asset: File, assetType: AssetType): void;
}

type RemovedType = "ANNOTATION" | "ASSET";

export const Comment = ({
    annotation,
    canEdit,
    selected,
    onContainerSelect,
    onRemoveAnnotation,
    onRemoveAsset,
    onUpdate,
    onIsReviewedUpdate,
    onAttachAsset,
}: CommentProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { assetId, isVisualVisible } = useMaterialSpecs();
    const [comment, setComment] = useState<string>(annotation.comment);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
    const [removedType, setRemovedType] = useState<RemovedType>("ANNOTATION");
    const debouncedComment = useDebounce(comment);
    const containerRef = useRef<HTMLInputElement>(null);
    const textAreaRef = useRef<HTMLInputElement>(null);
    const { REVIEW, DELETE } = usePermissions(annotation.permissions);
    const [downloadAsset] = useDownloadAsset();
    const { isProcessing } = useSelector(getAnnotationsStatus);
    const { validationError } = useAnnotationsHandler();

    const handleColorSelection = useCallback(
        (color: DropdownMenuItem) => {
            const updatedAnnotation = { ...annotation, color: color.id, modified: true };
            onUpdate(updatedAnnotation);
        },
        [annotation, onUpdate]
    );

    const handleIsReviewedChange = useCallback(() => {
        const updatedAnnotation = { ...annotation, reviewed: !annotation.reviewed };
        onIsReviewedUpdate(updatedAnnotation);
    }, [annotation, onIsReviewedUpdate]);

    const scrollAnnotationContainerIntoView = useCallback((containerRef: HTMLInputElement) => {
        if (!isElementInViewport(containerRef.getBoundingClientRect())) {
            containerRef.scrollIntoView();
        }
    }, []);

    const focusInput = useCallback((inputRef: HTMLInputElement) => {
        // todo: better way?
        // move to the end of event loop so newly added annotation can be rendered and then gain focus
        setTimeout(() => {
            inputRef.focus();
            // Move cursor to the end
            const { length } = inputRef.value;
            inputRef.selectionStart = length;
            inputRef.selectionEnd = length;
        }, 0);
    }, []);

    useEffect(() => {
        if (selected && containerRef.current && textAreaRef.current) {
            scrollAnnotationContainerIntoView(containerRef.current);
            focusInput(textAreaRef.current);
        }
    }, [selected, scrollAnnotationContainerIntoView, focusInput]);

    useEffect(() => {
        if (debouncedComment !== annotation.comment) {
            const updatedAnnotation = { ...annotation, comment: debouncedComment, modified: true };
            onUpdate(updatedAnnotation);
        }
        //eslint-disable-next-line
    }, [debouncedComment]);

    const handleOnConfirm = useCallback(
        (isConfirmed: boolean) => {
            if (isConfirmed) {
                if (removedType === "ANNOTATION") {
                    onRemoveAnnotation(annotation);
                    return;
                }

                onRemoveAsset(annotation.attachedAsset?.id || -1, annotation.selectionId as number);
            }

            setIsConfirmationOpen(false);
        },
        [annotation, onRemoveAnnotation, onRemoveAsset, removedType]
    );

    const handleOnDeleteClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, typeToRemove: RemovedType) => {
            setRemovedType(typeToRemove);
            // if the user holds Shift key while clicking delete, it will remove annotation immediately,
            // without popping up confirmation dialog
            if (event.shiftKey) {
                handleOnConfirm(true);

                return;
            }

            setIsConfirmationOpen(true);
        },
        [handleOnConfirm]
    );

    const handleFileAttachment = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            onAttachAsset(annotation.selectionId as number, e.target.files[0], assetId ? "VISUAL" : "DEFAULT");
        }
    };

    return (
        <LoadingOverlay active={isProcessing} visible={isProcessing}>
            <CommentContainer
                onClick={() => onContainerSelect(annotation)}
                error={validationError && annotation.comment === "" ? 1 : 0} // workaround for styled components bug regarding receiving "true/false for a non-boolean attribute" https://github.com/styled-components/styled-components/issues/1198
                selected={selected}
                ref={containerRef}>
                <Box display="flex">
                    <Box mr={1} flex={1}>
                        {/* show only for saved annotations and if has permission */}
                        {annotation.id !== 0 && REVIEW?.display && (
                            <Tooltip
                                title={t("campaign.annotations.label.isReviewed") || ""}
                                interactive
                                placement="top">
                                <Checkbox
                                    color="primary"
                                    disableRipple={true}
                                    onChange={handleIsReviewedChange}
                                    checked={annotation.reviewed}
                                    tabIndex={-1}
                                />
                            </Tooltip>
                        )}
                    </Box>
                    <Box flex={18}>
                        <Box mb={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box mb={2}>
                                    <Typography variant="body1" component="div">
                                        <Box fontWeight="fontWeightBold">{annotation.creator}</Box>
                                    </Typography>
                                    <Typography variant="body2">{annotation.date}</Typography>
                                </Box>

                                {annotation.timeStampSeconds !== undefined && (
                                    <Box maxWidth={theme.spacing(8)}>
                                        <TextField
                                            value={toHHMMSS(annotation.timeStampSeconds)}
                                            disabled
                                            InputProps={{
                                                endAdornment: <ScheduleIcon fontSize="small" />,
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>

                            <TextField
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                backgroundColor="white"
                                inputRef={textAreaRef}
                                disabled={!canEdit}
                            />
                        </Box>

                        <Box display="flex" justifyContent="flex-end">
                            {annotation.attachedAsset && (
                                <Box width="100%" alignSelf="flex-start">
                                    <Chip
                                        label={annotation.attachedAsset.fileName}
                                        size="small"
                                        clickable={false}
                                        onClick={() => {
                                            if (annotation.attachedAsset) {
                                                downloadAsset(annotation.attachedAsset.id);
                                            }
                                        }}
                                        onDelete={(event) => handleOnDeleteClick(event, "ASSET")}
                                    />
                                </Box>
                            )}
                            {canEdit && !annotation.attachedAsset && !isVisualVisible && (
                                <Tooltip
                                    title={t("campaign.annotations.label.attach") || ""}
                                    interactive
                                    placement="top">
                                    <IconButton component="label" tabIndex={-1} disableRipple>
                                        <AttachFileIcon />
                                        <input type="file" hidden onChange={handleFileAttachment} />
                                    </IconButton>
                                </Tooltip>
                            )}

                            <ButtonDropdownColors
                                label={<ColorLensIcon />}
                                labelColor={theme.palette.grey["600"]}
                                items={ANNOTATIONS_FRAME_COLORS}
                                onSelect={handleColorSelection}
                                skipTabIndexing={true}
                            />

                            {/* if has permission to remove existing annotations, or is newly added */}
                            {(DELETE?.display || annotation.id === 0) && (
                                <Tooltip
                                    title={t("campaign.annotations.label.delete") || ""}
                                    interactive
                                    placement="top">
                                    <IconButton
                                        onClick={(event) => handleOnDeleteClick(event, "ANNOTATION")}
                                        tabIndex={-1}
                                        disableRipple>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Box>

                <ConfirmationDialog
                    isOpen={isConfirmationOpen}
                    onClose={(isConfirmed) => handleOnConfirm(isConfirmed)}
                    onExited={() => setIsConfirmationOpen(false)}>
                    <Typography variant={"body2"}>
                        {removedType === "ANNOTATION"
                            ? t("campaign.annotations.label.deleteConfirmation")
                            : t("campaign.annotations.label.deleteAssetConfirmation")}
                    </Typography>
                </ConfirmationDialog>
            </CommentContainer>
        </LoadingOverlay>
    );
};
